import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { useTranslation } from 'react-i18next';
import { RedirectToLogin, RequiredAuthProvider } from '@propelauth/react';

import M3 from '@M3/M3';
import DisplaySettings from '@Providers/DisplaySettings';
import ApiProvider from '@Providers/ApiProvider';
import { RouterProvider } from '@tanstack/react-router';
import Loading from '@Components/General/Feedback/Loading';
import DialogProvider from '@Providers/DialogProvider';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import setMutationDefaults from '@utils/mutationDefaults';
import router, { queryClient } from '@router';
import DynamicUIProvider from '@Providers/DynamicUIProvider';

const rtlCache = createCache({
    key: 'muirtl',
    stylisPlugins: [prefixer, rtlPlugin],
});

const ltrCache = createCache({
    key: 'mui',
});

setMutationDefaults(queryClient);

function App() {
    const { i18n } = useTranslation();
    const rtl = i18n.dir() === 'rtl';

    return (
        <QueryClientProvider client={queryClient}>
            <CacheProvider value={rtl ? rtlCache : ltrCache}>
                <M3>
                    <RequiredAuthProvider
                        authUrl={import.meta.env.VITE_AUTH_URL}
                        displayWhileLoading={<Loading size={192} />}
                        displayIfLoggedOut={
                            <RedirectToLogin
                                postLoginRedirectUrl={window.location.href}
                            />
                        }
                    >
                        <ApiProvider router={router}>
                            <DynamicUIProvider>
                                <DialogProvider>
                                    <DisplaySettings>
                                        <RouterProvider router={router} />
                                    </DisplaySettings>
                                </DialogProvider>
                            </DynamicUIProvider>
                        </ApiProvider>
                    </RequiredAuthProvider>
                </M3>
            </CacheProvider>
            {localStorage.dev !== 'off' && (
                <ReactQueryDevtools position="right" />
            )}
        </QueryClientProvider>
    );
}

export default App;
